import {getType, SnapshotIn} from "mobx-state-tree";
import {QuizModel, QuizModelEnv, QuizModelType} from "./QuizModel";
import {QuizGqlProvider} from "./QuizGqlProvider";
import {QuizQuestionGQLProvider} from "./QuizQuestionGQLProvider";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import type {GQLQueryProvider} from "../gqlModel/GQLQueryProvider";

/**
 * Dependencies required to create a QuizModel with its environment.
 */
export interface CreateQuizModelDeps {
    dataClientService: IDataClientService;
    authService: IAuthService;
    getCurrentUserId: () => string | null | undefined;
}

/**
 * Creates a QuizModelEnv with the standard getQueryProvider implementation.
 * This centralizes the logic for routing queries to the appropriate provider based on node type.
 * 
 * @param deps - Service dependencies required for the environment
 * @returns Configured QuizModelEnv ready for use with QuizModel.create()
 */
export function createQuizModelEnv(deps: CreateQuizModelDeps): QuizModelEnv {
    const {dataClientService, authService, getCurrentUserId} = deps;

    const quizQueryProvider = new QuizGqlProvider({
        dataClientService,
        authService,
        getCurrentUserId,
    });

    const quizQuestionQueryProvider = new QuizQuestionGQLProvider({
        dataClientService,
        authService,
    });

    return {
        getDataClientService: () => dataClientService,
        authService,
        getQueryProvider: (node): GQLQueryProvider => {
            try {
                const typeName = getType(node).name;
                if (typeName === 'QuizModel') {
                    return quizQueryProvider;
                }
                if (typeName === 'QuizQuestionModel') {
                    return quizQuestionQueryProvider;
                }
            } catch (error) {
                // If getType fails, fall back to quiz provider
                return quizQueryProvider;
            }
            return quizQueryProvider;
        }
    };
}

/**
 * Creates a QuizModel instance with a properly configured environment.
 * This is the recommended way to create QuizModel instances throughout the application.
 * 
 * @param snapshot - Initial snapshot data for the QuizModel
 * @param deps - Service dependencies required for the environment
 * @returns Fully configured QuizModel instance ready to use
 * 
 * @example
 * ```typescript
 * const quizMST = createQuizModelWithEnv(
 *     quizServerToQuizMSTSnapshotIn(quizData),
 *     {
 *         dataClientService,
 *         authService,
 *         getCurrentUserId: () => userState.currentUserId
 *     }
 * );
 * ```
 */
export function createQuizModelWithEnv(
    snapshot: SnapshotIn<typeof QuizModel>,
    deps: CreateQuizModelDeps
): QuizModelType {
    const env = createQuizModelEnv(deps);
    return QuizModel.create(snapshot, env);
}
